import { EventEmitter } from '@djs';

export type AudioPlayerStatus = 'loading' | 'playing' | 'paused' | 'ended' | 'failed' | 'destroyed';

type VoiceMessageEvents = 'statusChanged' | 'playbackManipulation' | 'error';

type VoiceMessageEmitterEvents = Record<VoiceMessageEvents, VoidFunction>;

interface AudioPlayerControllerParams {
  autoplay?: boolean;
  speed?: number;
}

/**
 * Класс управления состоянием одного конкретного аудио
 * Хранит текущий url, waveform и идентификатор текущего аудио
 */
export class AudioPlayerController {
  private readonly emitter: EventEmitter<VoiceMessageEmitterEvents> = new EventEmitter();
  on = this.emitter.on;
  off = this.emitter.off;

  status: AudioPlayerStatus = 'loading';

  private setStatus(status: AudioPlayerStatus) {
    if (this.status !== status) {
      this.status = status;
      this.emitter.emit('statusChanged');
    }
  }

  private element: HTMLAudioElement;

  constructor(url: string, params?: AudioPlayerControllerParams) {
    const { autoplay, speed = 1 } = params || {};
    this.element = this.initAudio(url, autoplay);
    this.speed = speed;
  }

  get speed() {
    return this.element.playbackRate;
  }

  set speed(value) {
    this.element.playbackRate = value;
  }

  get paused() {
    return Boolean(this.element.paused);
  }

  get ended() {
    return Boolean(this.element.ended);
  }

  get duration() {
    return this.element.duration || 0;
  }

  get currentTime() {
    return this.ended ? this.duration : this.element.currentTime;
  }

  set currentTime(value: number) {
    this.element.currentTime = value;
  }

  reload() {
    this.setStatus('loading');
    const prevSpeed = this.speed;
    this.element.load();
    this.speed = prevSpeed;
  }

  private initAudio(url: string, autoplay?: boolean) {
    const audioElement = new Audio(url);
    audioElement.src = url;
    audioElement.controls = false;
    audioElement.preload = 'metadata';

    this.setStatus('loading');

    audioElement.onloadedmetadata = () => {
      if (autoplay) {
        this.play();
      } else {
        this.pause();
      }
    };

    audioElement.onplay = () => {
      this.setStatus('playing');
      this.emitter.emit('playbackManipulation');
    };

    audioElement.onpause = () => {
      this.setStatus('paused');
      this.emitter.emit('playbackManipulation');
    };

    audioElement.onended = () => {
      this.setStatus('ended');
    };

    audioElement.onerror = () => {
      this.setStatus('failed');
      this.emitter.emit('error');
    };

    return audioElement;
  }

  play() {
    this.element.play().catch(() => null);
  }

  pause() {
    this.element.pause();
  }

  togglePlaying() {
    if (this.status === 'failed') {
      this.reload();
    } else if (this.status === 'loading') {
      // nothing
    } else if (this.status === 'paused' || this.status === 'ended') {
      this.play();
    } else {
      this.pause();
    }
  }

  destroy() {
    if (!this.element.paused) {
      this.element.pause();
      this.emitter.emit('playbackManipulation');
    }
    this.emitter.reset();
    this.element.onerror = () => null;
    this.element.onplay = () => null;
    this.element.onloadedmetadata = () => null;
    this.element.onpause = () => null;
    this.element.onended = () => null;
    this.element.src = '';
    this.element.remove();
    this.setStatus('destroyed');
  }
}
